import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ** MUI Components
import { Box, Typography, Tooltip, IconButton, } from "@mui/material";

// ** Custom Component Imports
import CustomChip from "../../../components/mui/chip";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** API Services
import { GetAssets, DeleteAsset } from "./AssetService";
import { GetMaterials, DeleteMaterial } from "./MaterialService";
import { GetTypeCatalog } from "./TypeCatalogService";
import { DeleteTypeCatalog } from "./TypeCatalogService";
import { useInventoryPermission } from "../permissions/InventoryPermissions";

import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import "components/styles/swalstyles.css";
import { useTheme } from "@mui/material/styles";

const getColumnDefinitions = (
  handleEditAsset,
  handleDeleteAsset,
  handleEditAssetType,
  handleDeleteAssetType,
  handleEditMaterial,
  handleDeleteMaterial,
  handleEditMaterialType,
  handleDeleteMaterialType,
  InventoryPermissionProps
) => {
    //#region Permissions
    const {
      // Asset Permissions
      hasAssetSavePermission,
      hasAssetDeletePermission,
  
      // Material Permissions
      hasMaterialSavePermission,
      hasMaterialDeletePermission,
  
      // Asset & Material Type Permissions
      hasTypeCatalogSavePermission,
      hasTypeCatalogDeletePermission,
    } = InventoryPermissionProps;
    //#endregion Permissions

  const columnsAsset = [
    {
      flex: 0.1,
      minWidth: 140,
      field: "assetName",
      headerName: "Asset Name",
      sortable: true,
      sortingOrder: ['asc', 'desc'],
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1" 
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'text.primary',
                    textDecoration: 'underline',
                  }
                }}
                onClick={() => handleEditAsset(row)}
              >
                {row.assetName}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "assetType",
      headerName: "Asset Type",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.assetType}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "serialVINNumber",
      headerName: "Serial/VIN Number",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.serialVINNumber}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "location",
      headerName: "Company Location",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.location || "N/A"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "createdBy",
      headerName: "Created By",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.createdBy || "N/A"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "createDate",
      headerName: "Created On",
      renderCell: (params) => {
        const { row } = params;
        const formattedDate = row.createDate ? new Date(row.createDate).toLocaleDateString() : "N/A";

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {formattedDate}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 120,
      renderCell: ({ row }) => {
        //  console.log("isActive:", row.isActive);

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={row.status === 'Available' ? "success" : "secondary"} //'success' // Use color names: primary, secondary, error, warning, info, success
            label={row.status}
          />
        );
      },
    },
    {
      flex: 0.1,
      field: "actions",
      minWidth: 120,
      maxWidth: 120,
      headerName: "Action",
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <IconButton 
              disabled={!hasAssetSavePermission}
              {...(hasAssetSavePermission && {
                onClick: () => handleEditAsset(params.row)
              })}
            >
              <Icon icon="tabler:pencil" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton 
              color="error"
              disabled={!hasAssetDeletePermission}
              {...(hasAssetDeletePermission && {
                onClick: () => handleDeleteAsset(params.row.assetID)
              })}
            >
              <Icon icon="tabler:trash" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const columnsMaterial = [
      {
        flex: 0.1,
        minWidth: 140,
        field: "itemName",
        headerName: "Material Name",
        sortable: true,
      sortingOrder: ['asc', 'desc'],
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'text.primary',
                      textDecoration: 'underline',
                    }
                  }}
                  onClick={() => handleEditMaterial(params.row)}
                >
                {row.itemName}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
        field: "materialType",
        headerName: "Material Type",
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                {row.materialType}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
        field: "scheduledQuantity",
        headerName: "Scheduled",
          description:
        'Quantity scheduled to be used in upcoming jobs.',
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                  {row.scheduledQuantity || 0} {row.unitSymbol ? row.unitSymbol : row.unitName}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
        field: "warehouseQuantity",
        headerName: "Warehoused",
        description:
          'Total stock currently available in the warehouse.',
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                  {row.warehouseQuantity || 0} {row.unitSymbol ? row.unitSymbol : row.unitName}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
        field: "quantityInStock",
        headerName: "Quantity",
        description: 'Quantity',
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                  {row.quantityInStock} {row.unitSymbol ? row.unitSymbol : row.unitName}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
        field: "location",
        headerName: "Location",
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                  {row.location || "N/A"}
                  </Typography>
                </Box>
              </Box>
            );
        },
      },
      {
        flex: 0.1,
        field: "status",
        headerName: "Status",
        minWidth: 120,
        renderCell: ({ row }) => {
          return (
            <CustomChip
              rounded
              size="small"
              skin="light"
              sx={{ lineHeight: 1 }}
            color={row.isActive ? "success" : "secondary"} 
              label={row.isActive ? "Available" : "Unavailable"}
            />
          );
        },
      },
      {
        flex: 0.1,
        field: "actions",
        minWidth: 120,
        maxWidth: 120,
        headerName: "Action",
        renderCell: (params) => (
          <>
            <Tooltip title="Edit">
              <IconButton 
                disabled={!hasMaterialSavePermission}
                {...(hasMaterialSavePermission && {
                  onClick: () => handleEditMaterial(params.row)
                })}
              >
                <Icon icon="tabler:pencil" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove">
              <IconButton 
                color="error"
                disabled={!hasMaterialDeletePermission}
                {...(hasMaterialDeletePermission && {
                  onClick: () => handleDeleteMaterial(params.row.materialID)
                })}
              >
                <Icon icon="tabler:trash" />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
  ];

  const columnsMaterialType = [
      {
        flex: 0.1,
        minWidth: 140,
      field: "typeName",
        headerName: "Material Type",
        sortable: true,
      sortingOrder: ['asc', 'desc'],
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'text.primary',
                      textDecoration: 'underline',
                    }
                  }}
                  onClick={() => handleEditMaterialType(params.row)}
                >
                {row.typeName}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
      field: "description",
      headerName: "Description",
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                {row.description}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
      field: "createdBy",
      headerName: "Created By",
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                {row.createdBy || "N/A"}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
      field: "createDate",
      headerName: "Created On",
        renderCell: (params) => {
          const { row } = params;
        const formattedDate = row.createDate ? new Date(row.createDate).toLocaleDateString() : "N/A";

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                {formattedDate}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        field: "actions",
        minWidth: 120,
      maxWidth: 120,
        headerName: "Action",
      renderCell: (params) => (
          <>
            <Tooltip title="Edit">
              <IconButton 
                disabled={!hasTypeCatalogSavePermission}
                {...(hasTypeCatalogSavePermission && {
                  onClick: () => handleEditMaterialType(params.row)
                })}
              >
                <Icon icon="tabler:pencil" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove">
              <IconButton 
                color="error"
                disabled={!hasMaterialDeletePermission}
                {...(hasMaterialDeletePermission && {
                  onClick: () => handleDeleteMaterialType(params.row.typeCatalogID)
                })}
              >
                <Icon icon="tabler:trash" />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
  ];

  return {
    columnsAsset,
    columnsAssetType: [
      {
        flex: 0.1,
        minWidth: 140,
        field: "typeName",
        headerName: "Asset Type",
        sortable: true,
      sortingOrder: ['asc', 'desc'],
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'text.primary',
                      textDecoration: 'underline',
                    }
                  }}
                  onClick={() => handleEditAssetType(params.row)}
                >
                  {row.typeName}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
        field: "description",
        headerName: "Description",
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                  {row.description}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
        field: "createdBy",
        headerName: "Created By",
        renderCell: (params) => {
          const { row } = params;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                  {row.createdBy || "N/A"}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        minWidth: 140,
        field: "createDate",
        headerName: "Created On",
        renderCell: (params) => {
          const { row } = params;
          const formattedDate = row.createDate ? new Date(row.createDate).toLocaleDateString() : "N/A";

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography noWrap variant="body1">
                  {formattedDate}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        flex: 0.1,
        field: "status",
        headerName: "Status",
        minWidth: 120,
        renderCell: ({ row }) => {
          return (
            <CustomChip
              rounded
              size="small"
              skin="light"
              sx={{ lineHeight: 1 }}
              color={row.isActive ? "success" : "secondary"} //'success' // Use color names: primary, secondary, error, warning, info, success
              label={row.isActive ? "Available" : "Unavailable"}
            />
          );
        },
      },
      {
        flex: 0.1,
        field: "actions",
        minWidth: 120,
        maxWidth: 120,
        headerName: "Action",
        renderCell: (params) => (
          <>
            <Tooltip title="Edit">
              <IconButton 
                disabled={!hasTypeCatalogSavePermission}
                {...(hasTypeCatalogSavePermission && {
                  onClick: () => handleEditAssetType(params.row)
                })}
              >
                <Icon icon="tabler:pencil" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove">
              <IconButton 
                color="error"
                disabled={!hasTypeCatalogDeletePermission}
                {...(hasTypeCatalogDeletePermission && {
                  onClick: () => handleDeleteAssetType(params.row.typeCatalogID)
                })}
              >
                <Icon icon="tabler:trash" />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
    ],
    columnsMaterial,
    columnsMaterialType
  };
};

export const useIndexLogic = () => {
  //#region Permissions
  const { InventoryPermissionProps } = useInventoryPermission();
  const {
    // Asset Permissions
    hasAssetSavePermission,
    hasAssetGetPermission,
    hasAssetDeletePermission,

    // Material Permissions
    hasMaterialSavePermission,
    hasMaterialGetPermission,
    hasMaterialDeletePermission,

    // Asset & Material Type Permissions
    hasTypeCatalogSavePermission,
    hasTypeCatalogGetPermission,
    hasTypeCatalogDeletePermission,
  } = InventoryPermissionProps;
  //#endregion Permissions

  //#region Hooks
  const navigate = useNavigate();
  //#endregion
  
  //#region Constants
  const theme = useTheme();
  const MySwal = withReactContent(Swal);
  const contentHeightFixed = false;
  //#endregion End of Constants

  //#region States

  const [loading, setLoading] = useState(true);
  const [collapse, setCollapse] = useState(false);
  const [tabValue, setTabValue] = useState("1");
  const [tabTitle, setTabTitle] = useState('Assets')
  
  // Assets
  const [assets, setAssets] = useState([]);
  const [assetIsEmpty, setAssetIsEmpty] = useState(true);
  const [openAsset, setOpenAsset] = useState(false);
  const [openAssetDlg, setOpenAssetDlg] = useState(false);
  const [openAssetTypeDlg, setOpenAssetTypeDlg] = useState(false);
  const [assetTypes, setAssetTypes] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedAssetType, setSelectedAssetType] = useState(null);

  
  // Materials
  const [materialIsEmpty, setMaterialIsEmpty] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [openMaterial, setOpenMaterial] = useState(false);
  const [openMaterialDlg, setOpenMaterialDlg] = useState(false);
  const [openMaterialTypeDlg, setOpenMaterialTypeDlg] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedMaterialType, setSelectedMaterialType] = useState(null);

  //#endregion end of states

  //#region Handlers and methods

  useEffect(() => {
    // Load asset types first, then assets
    loadData();
  }, [hasTypeCatalogGetPermission, hasAssetGetPermission, hasMaterialGetPermission]);

  // Create a new function to load data sequentially
const loadData = async () => {
  try {
    setLoading(true);

    // ----------------- Asset Types & Assets -----------------
    if (hasTypeCatalogGetPermission) {
      const assetTypesResponse = await GetTypeCatalog("asset");
      if (!assetTypesResponse.isError && assetTypesResponse.result) {
        const assetTypesWithId = assetTypesResponse.result
          .map(type => ({
            ...type,
            id: type.typeCatalogID
          }))
          .sort((a, b) => a.typeName?.localeCompare(b.typeName));
        
        setAssetTypes(assetTypesWithId);

        if (hasAssetGetPermission) {
          const assetsResponse = await GetAssets();
          if (!assetsResponse.isError && assetsResponse.result) {
            const assetsWithAssetType = assetsResponse.result.map(asset => {
              const assetType = assetTypesWithId.find(
                type => type.typeCatalogID === asset.assetTypeID
              );
              return {
                ...asset,
                id: asset.assetID,
                assetType: assetType ? assetType.typeName : "Unknown"
              };
            });

            const sortedAssets = assetsWithAssetType.sort((a, b) => 
              a.assetName?.localeCompare(b.assetName)
            );

            setAssets(sortedAssets);
            setAssetIsEmpty(sortedAssets.length === 0);
          } else {
            console.error("Error fetching assets:", assetsResponse.message);
        //     toast.error("Failed to load assets", {
        //   style: {
        //     fontFamily: '"DM Sans", sans-serif',
        //     fontSize: '15px',
        //   },
        // });
          }
        }
      } else {
        console.error("Error fetching asset types:", assetTypesResponse.message);
        toast.error("Failed to load asset types", {
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
      }
    }

    // ----------------- Material Types & Materials -----------------
    if (hasTypeCatalogGetPermission) {
      const materialTypesResponse = await GetTypeCatalog("Material");
      if (!materialTypesResponse.isError && materialTypesResponse.result) {
        const materialTypesWithId = materialTypesResponse.result
          .map(type => ({
            ...type,
            id: type.typeCatalogID,
            typeCatalogID: type.typeCatalogID
          }))
          .sort((a, b) => a.typeName?.localeCompare(b.typeName));
        
        setMaterialTypes(materialTypesWithId);

        if (hasMaterialGetPermission) {
          const materialsResponse = await GetMaterials();
          if (!materialsResponse.isError && materialsResponse.result) {
            const materialsWithMaterialType = materialsResponse.result.map(material => {
              const materialType = materialTypesWithId.find(type => {
                if (typeof material.materialTypeID === "string") {
                  return String(type.typeCatalogID) === material.materialTypeID;
                }
                return type.typeCatalogID === material.materialTypeID;
              });
              return {
                ...material,
                id: material.materialID,
                materialType: materialType ? materialType.typeName : "Unknown"
              };
            });
            const sortedMaterials = materialsWithMaterialType.sort((a, b) => 
              a.itemName?.localeCompare(b.itemName)
            );

            setMaterials(sortedMaterials);
            setMaterialIsEmpty(sortedMaterials.length === 0);
          } else {
            console.error("Error fetching materials:", materialsResponse.message);
        //     toast.error("Failed to load materials", {
        //   style: {
        //     fontFamily: '"DM Sans", sans-serif',
        //     fontSize: '15px',
        //   },
        // });
          }
        }
      } else {
        console.error("Error fetching material types:", materialTypesResponse.message);
    //     toast.error("Failed to load material types", {
    //   style: {
    //     fontFamily: '"DM Sans", sans-serif',
    //     fontSize: '15px',
    //   },
    // });
      }
    }
  } catch (error) {
    console.error("Error loading data:", error);
    toast.error("An error occurred while loading data", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
  } finally {
    setLoading(false);
  }
};


  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if(newValue === '1'){
        setTabTitle('Assets')
    }else{
        setTabTitle('Materials')
    }
  };

  const handleEditAssetType = (assetType) => {
    setSelectedAssetType(assetType);
    setOpenAssetTypeDlg(true);
  };
  
  // Handler to delete asset type:
  const handleDeleteAssetType = async (typeCatalogID) => {
    if(!hasTypeCatalogDeletePermission) return;

    try {
     const result = await MySwal.fire({
    title: '<strong>Are you sure?</strong>',
    html: '<span>You won\'t be able to revert this!</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, keep it',
     width: '390px', // Explicit width control
  padding: '1rem',
    focusConfirm: false,
     showCloseButton: true,
     closeButtonHtml: '&times;', // This is the × symbol
    customClass: {
     popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
         cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
    }
  });
  
      if (result.isConfirmed) {
        setLoading(true);
        const response = await DeleteTypeCatalog(typeCatalogID, "Asset");
        if (!response.isError) {
          // Remove the deleted asset type from state (you may need to refresh by calling loadData)
          setAssetTypes(assetTypes.filter(a => a.typeCatalogID !== typeCatalogID));
          toast.success("Asset type deleted successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        } else {
          toast.error(response.message || "Failed to delete asset type", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        }
      }
    } catch (error) {
      console.error("Error deleting asset type:", error);
      toast.error("An unexpected error occurred while deleting asset type", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });

    } finally {
      setLoading(false);
    }
  };

  const handleEditAsset = (asset) => {
    setSelectedAsset(asset);
    setOpenAssetDlg(true);
  };

  const handleDeleteAsset = async (assetID) => {
    if(!hasAssetDeletePermission) return;

    try {
      // Confirmation dialog
     const result = await MySwal.fire({
    title: '<strong>Are you sure?</strong>',
    html: '<span>You won\'t be able to revert this!</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, keep it',
     width: '390px', // Explicit width control
  padding: '1rem',
    focusConfirm: false,
     showCloseButton: true,
     closeButtonHtml: '&times;', // This is the × symbol
    customClass: {
      popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
      title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
      htmlContainer: 'custom-swal-html',
      confirmButton: 'custom-swal-confirm',
     cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
       closeButton: 'custom-swal-close'
    }
  });

      if (result.isConfirmed) {
        setLoading(true);
        const response = await DeleteAsset(assetID);
        
        if (!response.isError) {
          // Remove the deleted asset from state
          setAssets(assets.filter(a => a.assetID !== assetID));
          setAssetIsEmpty(assets.length <= 1); // Update empty state if this was the last asset
          
          toast.success("Asset deleted successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });

        } else {
          toast.error(response.message || "Failed to delete asset", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });

        }
      }
    } catch (error) {
      console.error("Error deleting asset:", error);
      toast.error("An unexpected error occurred while deleting the asset", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });

    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssetDlg = () => setOpenAssetDlg(true);
  
  const handleCloseAssetDlg = () => {
    setOpenAssetDlg(false);
    setSelectedAsset(null);
  };
  
  const handleSaveAsset = () => {
    // First close the dialog safely
    setOpenAssetDlg(false);
    setSelectedAsset(null);
    
    // Then load data after a small delay to prevent race conditions
    setTimeout(() => {
      loadData();
    }, 100);
  };

  const handleOpenAssetTypeDlg = () => setOpenAssetTypeDlg(true);
  const handleCloseAssetTypeDlg = () => {
    setOpenAssetTypeDlg(false);
    setSelectedAssetType(null);

    setTimeout(() => {
      loadData();
    }, 100);
  };

  const handleOpenMaterialDlg = () => setOpenMaterialDlg(true);
  const handleCloseMaterialDlg = () => {
    setOpenMaterialDlg(false);
    setSelectedMaterial(null);
  };
  const handleSaveMaterial = () => {
    // First close the dialog safely
    setOpenMaterialDlg(false);
    setSelectedMaterial(null);
    
    // Then load data after a small delay to prevent race conditions
    setTimeout(() => {
      loadData();
    }, 100);
  };

  const handleOpenMaterialTypeDlg = () => setOpenMaterialTypeDlg(true);
  const handleCloseMaterialTypeDlg = () => {
    setOpenMaterialTypeDlg(false);
    setSelectedMaterialType(null);

    setTimeout(() => {
      loadData();
    }, 100);
  };

  // Material handlers
  const handleEditMaterialType = (materialType) => {
    setSelectedMaterialType(materialType);
    setOpenMaterialTypeDlg(true);
  };
  
  const handleDeleteMaterialType = async (typeCatalogID) => {
    if(!hasTypeCatalogDeletePermission) return;

    try {
       const result = await MySwal.fire({
    title: '<strong>Are you sure?</strong>',
    html: '<span>You won\'t be able to revert this!</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, keep it',
     width: '390px', // Explicit width control
  padding: '1rem',
    focusConfirm: false,
     showCloseButton: true,
     closeButtonHtml: '&times;', // This is the × symbol
    customClass: {
      popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
      title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
      htmlContainer: 'custom-swal-html',
      confirmButton: 'custom-swal-confirm',
     cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
       closeButton: 'custom-swal-close'
    }
  });
  
      if (result.isConfirmed) {
        setLoading(true);
        const response = await DeleteTypeCatalog(typeCatalogID, "Material");
        if (!response.isError) {
          // Remove the deleted material type from state
          setMaterialTypes(materialTypes.filter(m => m.typeCatalogID !== typeCatalogID));
          toast.success("Material type deleted successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        } else {
          toast.error(response.message || "Failed to delete material type", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        }
      }
    } catch (error) {
      console.error("Error deleting material type:", error);
      toast.error("An unexpected error occurred while deleting material type", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    } finally {
      setLoading(false);
    }
  };

  const handleEditMaterial = (material) => {
    setSelectedMaterial(material);
    setOpenMaterialDlg(true);
  };

  const handleDeleteMaterial = async (materialID) => {
    if(!hasMaterialDeletePermission) return;

    try {
      // Confirmation dialog
       const result = await MySwal.fire({
    title: '<strong>Are you sure?</strong>',
    html: '<span>You won\'t be able to revert this!</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, keep it',
     width: '390px', // Explicit width control
  padding: '1rem',
    focusConfirm: false,
     showCloseButton: true,
     closeButtonHtml: '&times;', // This is the × symbol
    customClass: {
      popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
      title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
      htmlContainer: 'custom-swal-html',
      confirmButton: 'custom-swal-confirm',
     cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
       closeButton: 'custom-swal-close'
    }
  });

      if (result.isConfirmed) {
        setLoading(true);
        const response = await DeleteMaterial(materialID);
        
        if (!response.isError) {
          // Remove the deleted material from state
          setMaterials(materials.filter(m => m.materialID !== materialID));
          setMaterialIsEmpty(materials.length <= 1); // Update empty state if this was the last material
          
          toast.success("Material deleted successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        } else {
          toast.error(response.message || "Failed to delete material", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        }
      }
    } catch (error) {
      console.error("Error deleting material:", error);
      toast.error("An unexpected error occurred while deleting the material", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  //#endregion end of handlers and methods

  //#region prepare column definitions
  
  const { columnsAsset, columnsAssetType, columnsMaterial, columnsMaterialType } = 
    getColumnDefinitions(
      handleEditAsset, 
      handleDeleteAsset, 
      handleEditAssetType, 
      handleDeleteAssetType,
      handleEditMaterial,
      handleDeleteMaterial,
      handleEditMaterialType,
      handleDeleteMaterialType,
      InventoryPermissionProps
    );
  
  //#endregion

  //#region IndexProps
  const IndexProps = {
    contentHeightFixed,
    loading,
    setLoading,
    collapse,
    setCollapse,
    tabValue,
    tabTitle,
    handleTabChange,
    
    // Asset states
    assetIsEmpty,
    assets,
    assetTypes,
    openAsset,
    openAssetDlg,
    openAssetTypeDlg,
    selectedAsset,
    selectedAssetType,
    
    // Material states
    materialIsEmpty,
    materials,
    materialTypes,
    openMaterial,
    openMaterialDlg,
    openMaterialTypeDlg,
    selectedMaterial,
    selectedMaterialType,
    
    // Column definitions
    columnsAsset,
    columnsAssetType,
    columnsMaterial,
    columnsMaterialType,
    
    // Asset handlers
    handleOpenAssetDlg,
    handleCloseAssetDlg,
    handleSaveAsset,
    handleOpenAssetTypeDlg,
    handleCloseAssetTypeDlg,
    handleEditAsset,
    handleDeleteAsset,
    handleEditAssetType,
    handleDeleteAssetType,
    
    // Material handlers
    handleOpenMaterialDlg,
    handleCloseMaterialDlg,
    handleOpenMaterialTypeDlg,
    handleCloseMaterialTypeDlg,
    handleSaveMaterial,
    handleEditMaterial,
    handleDeleteMaterial,
    handleEditMaterialType,
    handleDeleteMaterialType,
    
    // Other functions
    setOpenMaterial,
    loadData
  };
  //#endregion end of IndexProps

  return {
    IndexProps,
  };
};
